/**
 * External Imports
 */
import { FC, useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import {
  clearCensusReportFilter,
  clearPatientRoundHistory,
  clearTasksByAccountNum
} from '../../../../redux/actions';

import {
  logListClick,
  logRoundProfile
} from '../../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../../utils/debouncers';
import {
  calculateStayTime,
  reverseName,
  toTitleCase
} from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Patient } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  CompleteIcon,
  IconDiv,
  PatientInfoDiv,
  StayContainer,
  ToDoIcon,
  UnableStayLabel
} from './CensusTable.styles';

interface IPatientCensusTableProps {
  facilityId?: string;
  isLoading: boolean;
  patientsToRound: Patient[];
  searchTerm: string;
  selectedTab: string;
}

const PatientCensusTable: FC<IPatientCensusTableProps> = ({
  facilityId,
  isLoading,
  patientsToRound,
  searchTerm,
  selectedTab
}) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('lastRoundDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Last Round');

  const { logTrackingEvent } = useAnalyticsApi();

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const navigate = useNavigate();
  const { '*': view } = useParams();

  useEffect(() => {
    // clear census filters & reset page when navigating away
    return () => {
      dispatch(clearCensusReportFilter());
    };
  }, []);

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Patient':
        key = 'lastName';
        break;
      case 'Unit':
        key = 'unitId';
        break;
      case 'Room':
        key = 'room';
        break;
      case 'Stay':
        key = 'lengthOfStay';
        break;
      case 'Last Rounded By':
        key = 'lastRoundedBy';
        break;
      case 'Last Round':
        key = 'lastRoundDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openPatient = (patientToOpen: Patient) => {
    dispatch(clearPatientRoundHistory());
    dispatch(clearTasksByAccountNum());
    logTrackingEvent([...logListClick('table', view), ...logRoundProfile()]);
    const patientMeta = {
      facilityId: facilityId ?? '',
      accountNum: patientToOpen.accountNum,
      unitId: patientToOpen.unitId
    };
    if (patientToOpen.accountNum.substring(0, 2) === 'UL') {
      navigate('/patients/profile', {
        state: {
          patientMeta: { ...patientMeta, ...patientToOpen },
          isUnlistedPatient: true
        }
      });
    } else {
      navigate('/patients/profile', {
        state: {
          patientMeta,
          isUnlistedPatient: false
        }
      });
    }
  };

  const filteredPtr = useMemo(() => {
    const q = new RegExp(dbSearchTerm.trim().replace(/\\/g, ''), 'i');
    return patientsToRound.filter(
      patient =>
        patient.firstName.match(q) ||
        patient.lastName.match(q) ||
        `${patient.firstName} ${patient.lastName}`.match(q) ||
        `${patient.lastName} ${patient.firstName}`.match(q) ||
        `${patient.lastName}, ${patient.firstName}`.match(q) ||
        patient.mrn.match(q)
    );
  }, [patientsToRound, dbSearchTerm]);

  const sortedPtr = useMemo(() => {
    const stateCopy = [...filteredPtr];
    const sorted = stateCopy.sort((a: Patient, b: Patient) => {
      if (sortKey === 'lengthOfStay') {
        if (a[sortKey] === b[sortKey]) {
          return 0;
        }
        if (!a[sortKey]) {
          return sortDir === 'asc' ? 1 : -1;
        }
        if (!b[sortKey]) {
          return sortDir === 'asc' ? -1 : 1;
        }
        if (sortDir === 'asc') {
          return b[sortKey] - a[sortKey];
        }
        return a[sortKey] - b[sortKey];
      }
      if (a[sortKey] === b[sortKey]) {
        return 0;
      }
      if (!a[sortKey]) {
        return sortDir === 'asc' ? 1 : -1;
      }
      if (!b[sortKey]) {
        return sortDir === 'asc' ? -1 : 1;
      }
      if (sortDir === 'asc') {
        return a[sortKey]! > b[sortKey]! ? 1 : -1;
      }
      return a[sortKey]! > b[sortKey]! ? -1 : 1;
    });
    return sorted;
  }, [filteredPtr, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () =>
      !dbSearchTerm && sortedPtr.length === 0 && !(selectedTab === 'Completed'),
    [dbSearchTerm, sortedPtr, selectedTab]
  );
  const isTableEmptyCompleted = useMemo(
    () =>
      !dbSearchTerm && sortedPtr.length === 0 && selectedTab === 'Completed',
    [dbSearchTerm, sortedPtr, selectedTab]
  );
  const isTableEmptySearch = useMemo(
    () => !!dbSearchTerm && sortedPtr.length === 0,
    [dbSearchTerm, sortedPtr]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Patient"
      id="Census-Table-Patient-Column"
      unit="column"
      colWidth={25}
      direction={!(active === 'Patient') ? 'none' : sortDir}
      sort
    >
      Patient
    </CellContainer>,
    <CellContainer
      key="Unit"
      id="Census-Table-Unit-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Unit') ? 'none' : sortDir}
      sort
    >
      Unit
    </CellContainer>,
    <CellContainer
      key="Room"
      id="Census-Table-Room-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Room') ? 'none' : sortDir}
      sort
    >
      Room
    </CellContainer>,
    <CellContainer
      key="Stay"
      id="Census-Table-Stay-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Stay') ? 'none' : sortDir}
      sort
    >
      Stay
    </CellContainer>,
    <CellContainer
      key="Last Rounded By"
      id="Census-Table-Last-Rounded-By-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Last Rounded By') ? 'none' : sortDir}
      sort
    >
      Last Rounded By
    </CellContainer>,
    <CellContainer
      key="Last Round"
      id="Census-Table-Last-Round-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Last Round') ? 'none' : sortDir}
      sort
    >
      Last Round
    </CellContainer>
  ];

  const rows = sortedPtr.map(
    (patient: Patient, i: number) => (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Patient-Census-Row-${i}`}
        id={`Patient-Census-Row-${patient.mrn}`}
        unit="row"
        size="default"
        role="button"
        styles={
          patient.roundStatus === 'Complete'
            ? 'background-color: #f6f6f6 !important'
            : ''
        }
        onClick={() => openPatient(patient)}
      >
        <CellContainer unit="column" colWidth={25}>
          <PatientInfoDiv>
            <IconDiv>
              {patient.roundStatus === 'To Do' ? (
                <ToDoIcon className="neu-text-orange-50">circle</ToDoIcon>
              ) : (
                <CompleteIcon>check</CompleteIcon>
              )}
            </IconDiv>
            <StyledLabel className="neu-text-blue-70 bold">
              {toTitleCase(reverseName(patient.firstName, patient.lastName))}
            </StyledLabel>
          </PatientInfoDiv>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel>{patient.unitId}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>
            {patient.room}
            {patient.bed}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StayContainer>
            {patient.dischargetime || patient.transferred ? (
              <UnableStayLabel>
                {patient.transferred ? 'Transferred' : 'Discharged'}
              </UnableStayLabel>
            ) : (
              <StyledLabel>
                {patient.lengthOfStay !== -1
                  ? calculateStayTime(patient.lengthOfStay)
                  : ''}
              </StyledLabel>
            )}
          </StayContainer>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel>{patient.lastRoundedBy}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel>{patient.formattedLastRoundDate}</StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    )
    // *******************************************
    // ********* Removing till Phase II **********
    // *******************************************
    //     : (
    //   <UnableRow
    //     columns="{'Patient': '20%', 'Unit': '20%', 'Room': '10%', 'Stay': '10%', 'Last Rounded By': '20%', 'Last Round': '20%'}"
    //     size="large"
    //     onClick={click => openPatient(patient)}
    //     key={patient.mrn}
    //   >
    //     <NeuLabel color="gray" slot="Patient">
    //       {patient.firstName} {patient.lastName}
    //     </NeuLabel>
    //     <NeuLabel slot="Unit">{patient.unitId}</NeuLabel>
    //     <NeuLabel slot="Room">{patient.room}</NeuLabel>
    //     <UnableStayLabel slot="Stay">
    //       {patient.transferred ? 'Transferred' : 'Discharged'}
    //     </UnableStayLabel>
    //     <NeuLabel slot="Last Rounded By">
    //       {patient.lastRoundedBy}
    //     </NeuLabel>
    //     <NeuLabel slot="Last Round">
    //       {patient.formattedLastRoundDate}
    //     </NeuLabel>
    //   </UnableRow>
    // );
  );

  return (
    <Table
      customClass="patient-census-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableEmptySearch}
      isTableEmptyCompleted={isTableEmptyCompleted}
      loading={isLoading}
      type="patientCensus"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default PatientCensusTable;
